import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Capacitor } from "@capacitor/core";
import { queryClient } from "@/lib/queryClient";

export default function AuthGoogleCallback() {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<"working" | "failed" | "fallback">("working");

  const params = useMemo(
    () => new URLSearchParams(typeof window !== "undefined" ? window.location.search : ""),
    [],
  );
  const token = params.get("token") || "";
  const error = params.get("error");

  useEffect(() => {
    if (error || !token) {
      setStatus("failed");
      const t = window.setTimeout(() => setLocation("/login?error=google_failed"), 1500);
      return () => window.clearTimeout(t);
    }

    // Critical: only exchange the token if we're inside the wrapped app's
    // webview. If we're in CCT / SafariView / any browser, the session
    // cookie set by /api/auth/exchange-token goes into THAT cookie store,
    // not the wrapped app's. The user would be "logged in" in CCT but the
    // wrapped app's /api/auth/user still returns 401. Show a deep-link
    // button instead so the wrapped app handles the auth in its own
    // webview after the appUrlOpen event.
    if (!Capacitor.isNativePlatform()) {
      setStatus("fallback");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/auth/exchange-token", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        if (cancelled) return;
        if (!res.ok) {
          setStatus("failed");
          window.setTimeout(
            () => setLocation("/login?error=google_failed"),
            1500,
          );
          return;
        }
        await res.json().catch(() => undefined);
        await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        if (!cancelled) setLocation("/");
      } catch {
        if (!cancelled) {
          setStatus("failed");
          window.setTimeout(
            () => setLocation("/login?error=google_failed"),
            1500,
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [error, token, setLocation]);

  if (status === "fallback") {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isAndroid = /Android/i.test(ua);
    const customScheme = `marketlink://auth/google?token=${encodeURIComponent(token)}`;
    const href = isAndroid
      ? `intent://auth/google?token=${encodeURIComponent(token)}#Intent;scheme=marketlink;package=app.marketlink.mobile;end`
      : customScheme;

    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center">
        <div className="max-w-sm">
          <h1 className="mb-2 font-display text-3xl font-extrabold text-primary">
            Almost there
          </h1>
          <p className="mb-6 text-muted-foreground">
            Tap below to finish signing in to Stoop.
          </p>
          <a
            href={href}
            className="block rounded-xl bg-primary px-6 py-4 font-semibold text-primary-foreground shadow-brand"
          >
            Return to Stoop
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center">
      <p className="text-muted-foreground">
        {status === "working" ? "Signing you in…" : "Couldn't complete sign-in. Redirecting…"}
      </p>
    </div>
  );
}
